import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { 
  X, 
  Save, 
  Heart, 
  AlertTriangle,
  CheckCircle,
  Info,
  Filter,
  Leaf,
  Wheat,
  Fish,
  Milk,
  Nut,
  Egg
} from 'lucide-react'

interface DietaryPreferencesModalProps {
  isOpen: boolean
  onClose: () => void
  currentPreferences: string[]
  onSave: (preferences: string[]) => Promise<void>
}

const DietaryPreferencesModal: React.FC<DietaryPreferencesModalProps> = ({
  isOpen,
  onClose,
  currentPreferences,
  onSave
}) => {
  const [selectedPreferences, setSelectedPreferences] = useState<string[]>(currentPreferences)
  const [selectedAllergies, setSelectedAllergies] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'restrictions' | 'allergies'>('restrictions')

  const dietaryRestrictions = [
    {
      id: 'vegetarian',
      name: 'Vegetarian',
      description: 'No meat, poultry, or fish',
      icon: Leaf,
      color: 'bg-green-100 text-green-800 border-green-200'
    },
    {
      id: 'vegan',
      name: 'Vegan',
      description: 'No animal products including dairy and eggs',
      icon: Leaf,
      color: 'bg-emerald-100 text-emerald-800 border-emerald-200'
    },
    {
      id: 'gluten-free',
      name: 'Gluten-Free',
      description: 'No wheat, barley, rye, or derivatives',
      icon: Wheat,
      color: 'bg-orange-100 text-orange-800 border-orange-200'
    },
    {
      id: 'keto',
      name: 'Keto',
      description: 'Low carb, high fat diet',
      icon: Info,
      color: 'bg-purple-100 text-purple-800 border-purple-200'
    },
    {
      id: 'paleo',
      name: 'Paleo',
      description: 'No processed foods, grains, or dairy',
      icon: Info,
      color: 'bg-amber-100 text-amber-800 border-amber-200'
    },
    {
      id: 'low-carb',
      name: 'Low-Carb',
      description: 'Reduced carbohydrate intake',
      icon: Info,
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      id: 'high-protein',
      name: 'High-Protein',
      description: 'Emphasis on protein-rich foods',
      icon: Info,
      color: 'bg-indigo-100 text-indigo-800 border-indigo-200'
    },
    {
      id: 'dairy-free',
      name: 'Dairy-Free',
      description: 'No milk or milk products',
      icon: Milk,
      color: 'bg-pink-100 text-pink-800 border-pink-200'
    }
  ]

  const commonAllergens = [
    {
      id: 'peanuts',
      name: 'Peanuts',
      icon: Nut,
      severity: 'high',
      description: 'Can cause severe allergic reactions'
    },
    {
      id: 'tree-nuts',
      name: 'Tree Nuts',
      icon: Nut,
      severity: 'high',
      description: 'Includes almonds, walnuts, cashews, etc.'
    },
    {
      id: 'shellfish',
      name: 'Shellfish',
      icon: Fish,
      severity: 'high',
      description: 'Includes shrimp, crab, lobster, etc.'
    },
    {
      id: 'fish',
      name: 'Fish',
      icon: Fish,
      severity: 'medium',
      description: 'Fin fish such as salmon, tuna, cod'
    },
    {
      id: 'eggs',
      name: 'Eggs',
      icon: Egg,
      severity: 'medium',
      description: 'Chicken eggs and egg products'
    },
    {
      id: 'milk',
      name: 'Milk',
      icon: Milk,
      severity: 'medium',
      description: 'Cow\'s milk and dairy products'
    },
    {
      id: 'soy',
      name: 'Soy',
      icon: Info,
      severity: 'medium',
      description: 'Soybeans and soy products'
    },
    {
      id: 'wheat',
      name: 'Wheat',
      icon: Wheat,
      severity: 'medium',
      description: 'Wheat and gluten-containing grains'
    }
  ]

  useEffect(() => {
    setSelectedPreferences(currentPreferences)
  }, [currentPreferences])

  const togglePreference = (preferenceId: string) => {
    setSelectedPreferences(prev => 
      prev.includes(preferenceId)
        ? prev.filter(id => id !== preferenceId)
        : [...prev, preferenceId]
    )
  }

  const toggleAllergy = (allergyId: string) => {
    setSelectedAllergies(prev => 
      prev.includes(allergyId)
        ? prev.filter(id => id !== allergyId)
        : [...prev, allergyId]
    )
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave(selectedPreferences)
      onClose()
    } catch (error) {
      console.error('Error saving dietary preferences:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200'
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200'
      default: return 'bg-theme-surface text-theme-text-primary border-theme-border'
    }
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high': return <AlertTriangle className="w-4 h-4 text-red-600" />
      case 'medium': return <Info className="w-4 h-4 text-yellow-600" />
      case 'low': return <Info className="w-4 h-4 text-blue-600" />
      default: return <Info className="w-4 h-4 text-theme-text-muted" />
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative bg-theme-surface rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-theme-border"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-theme-border">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-theme-text-primary">Dietary Preferences</h2>
                    <p className="text-sm text-theme-text-secondary">Customize your meal recommendations</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-theme-text-muted hover:text-theme-text-primary hover:bg-theme-surface-light rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-theme-border">
                <button
                  onClick={() => setActiveTab('restrictions')}
                  className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'restrictions'
                      ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
                      : 'text-theme-text-secondary hover:text-theme-text-primary'
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Leaf className="w-4 h-4" />
                    <span>Dietary Restrictions</span>
                    {selectedPreferences.length > 0 && (
                      <Badge className="bg-green-100 text-green-800">
                        {selectedPreferences.length}
                      </Badge>
                    )}
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('allergies')}
                  className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'allergies'
                      ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                      : 'text-theme-text-secondary hover:text-theme-text-primary'
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Allergies</span>
                    {selectedAllergies.length > 0 && (
                      <Badge className="bg-red-100 text-red-800">
                        {selectedAllergies.length}
                      </Badge>
                    )}
                  </div>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {activeTab === 'restrictions' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-theme-text-primary">Choose your dietary restrictions</h3>
                      <Badge variant="secondary" className="bg-theme-surface-light text-theme-text-secondary">
                        {selectedPreferences.length} selected
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {dietaryRestrictions.map((restriction) => {
                        const Icon = restriction.icon
                        const isSelected = selectedPreferences.includes(restriction.id)
                        
                        return (
                          <motion.div
                            key={restriction.id}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <button
                              onClick={() => togglePreference(restriction.id)}
                              className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                                isSelected
                                  ? `${restriction.color} border-current`
                                  : 'bg-theme-surface border-theme-border hover:border-theme-primary/30 hover:bg-theme-surface-light'
                              }`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`p-2 rounded-lg ${
                                  isSelected ? 'bg-current bg-opacity-20' : 'bg-theme-surface-light'
                                }`}>
                                  <Icon className={`w-5 h-5 ${
                                    isSelected ? 'text-current' : 'text-theme-text-muted'
                                  }`} />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <h4 className="font-medium text-theme-text-primary">{restriction.name}</h4>
                                    {isSelected && (
                                      <CheckCircle className="w-4 h-4 text-current" />
                                    )}
                                  </div>
                                  <p className="text-sm text-theme-text-secondary mt-1">{restriction.description}</p>
                                </div>
                              </div>
                            </button>
                          </motion.div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {activeTab === 'allergies' && (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-theme-text-primary mb-2">Select allergens to avoid</h3>
                      <p className="text-sm text-theme-text-secondary mb-4">
                        We'll filter out meals that contain these ingredients and show appropriate warnings.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {commonAllergens.map((allergen) => {
                        const Icon = allergen.icon
                        const isSelected = selectedAllergies.includes(allergen.id)
                        
                        return (
                          <motion.div
                            key={allergen.id}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <button
                              onClick={() => toggleAllergy(allergen.id)}
                              className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                                isSelected
                                  ? `${getSeverityColor(allergen.severity)} border-current`
                                  : 'bg-theme-surface border-theme-border hover:border-theme-border-hover hover:bg-theme-surface-light'
                              }`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`p-2 rounded-lg ${
                                  isSelected ? 'bg-current bg-opacity-20' : 'bg-theme-surface-light'
                                }`}>
                                  <Icon className={`w-5 h-5 ${
                                    isSelected ? 'text-current' : 'text-theme-text-muted'
                                  }`} />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <h4 className="font-medium text-theme-text-primary">{allergen.name}</h4>
                                    {getSeverityIcon(allergen.severity)}
                                    {isSelected && (
                                      <CheckCircle className="w-4 h-4 text-current" />
                                    )}
                                  </div>
                                  <p className="text-sm text-theme-text-secondary mt-1">{allergen.description}</p>
                                </div>
                              </div>
                            </button>
                          </motion.div>
                        )
                      })}
                    </div>

                    {selectedAllergies.length > 0 && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                          <h4 className="font-medium text-amber-900">Important Safety Notice</h4>
                        </div>
                        <p className="text-sm text-amber-800">
                          While we filter meals based on your allergen selections, please always double-check 
                          ingredient lists and inform vendors about your allergies when ordering.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-theme-border bg-theme-surface-light">
                <div className="text-sm text-theme-text-secondary">
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4" />
                    <span>
                      {selectedPreferences.length + selectedAllergies.length} filters active
                    </span>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="border-theme-border text-theme-text-secondary hover:bg-theme-surface"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {isSaving ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Save className="w-4 h-4" />
                        <span>Save Preferences</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default DietaryPreferencesModal